import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { THEME_NAMES, themes, setThemeChoice, Modal } from '@threatlens/design-system';

/** Cmd-K command palette (spec section L): jump to hash/IOC/pages/actions. */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const trimmed = query.trim();
  const isHash = /^[a-fA-F0-9]{32,64}$/.test(trimmed);
  const actions = [
    ...(trimmed
      ? [
          {
            label: `Search intelligence for "${trimmed.slice(0, 32)}"`,
            run: () => nav(`/search?q=${encodeURIComponent(trimmed)}`),
          },
        ]
      : []),
    ...(isHash ? [{ label: `Open report for ${query.slice(0, 16)}…`, run: () => nav(`/report/${trimmed}`) }] : []),
    { label: 'Go to Submit', run: () => nav('/') },
    { label: 'Search threat intelligence', run: () => nav('/search') },
    { label: 'Go to Community feed', run: () => nav('/community') },
    { label: 'Open live malware firehose', run: () => nav('/firehose') },
    { label: 'Open graph explorer', run: () => nav('/graph') },
    { label: 'Go to Dashboard', run: () => nav('/dashboard') },
    // Theme switching: listed when relevant so the default list stays short.
    ...(!trimmed || /theme|light|dark|midnight|crimson|graphite|terminal|system/i.test(trimmed)
      ? [
          ...THEME_NAMES.map((n) => ({ label: `Theme: ${themes[n].label}`, run: () => setThemeChoice(n) })),
          { label: 'Theme: System (auto)', run: () => setThemeChoice('system') },
        ]
      : []),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (trimmed) {
      nav(isHash ? `/report/${trimmed}` : `/search?q=${encodeURIComponent(trimmed)}`);
      onOpenChange(false);
    }
  };

  return (
    // The palette's own Escape handler stays in the Cmd-K effect above, because it also owns the
    // OPEN shortcut; Modal's is harmless and idempotent.
    <Modal
      onClose={() => onOpenChange(false)}
      title="Command palette"
      hideTitle
      hideCloseButton
      align="top"
      size="xl"
      className="overflow-hidden border-border-strong bg-bg-overlay p-0 shadow-lg"
    >
        <form onSubmit={submit}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search hash, IOC, or type a command…"
            className="w-full border-b border-border-subtle bg-transparent px-4 py-3 text-text-primary outline-none placeholder:text-text-muted"
          />
        </form>
        <ul className="max-h-80 overflow-auto py-2">
          {actions.map((a) => (
            <li key={a.label}>
              <button
                onClick={() => {
                  a.run();
                  onOpenChange(false);
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-text-secondary hover:bg-accent-500/10 hover:text-text-primary"
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
    </Modal>
  );
}
